import { useCallback, useEffect, useState } from 'react';
import type { AuditLog, AuditResult, AuditTargetType, Paginated } from '@shared/types';
import { formatDateTime, relativeTime } from '@shared/format';
import { PageContainer, PageHeader } from '../../app/AppShell';
import { useAuthStore } from '../../app/authStore';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner } from '../../components/Card';
import { Select, TextInput } from '../../components/Field';
import { DataTable, type Column } from '../../components/Table';
import { toast } from '../../components/Toast';
import { overlay } from '../../overlay';
import { api, errorMessage } from '../../lib/api';

/**
 * 审计日志。
 *
 * 只记登录与管理员动作 —— 创意本身的每一步在详情页的「创意动态」里，
 * 两者分工明确：这里看的是「谁动了权限与配置」，那里看的是「这个创意怎么走到今天」。
 */
export function AuditPage() {
  const permissions = useAuthStore((state) => state.permissions);
  const canPrune = permissions.includes('settings.manage');

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [result, setResult] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await api.get<Paginated<AuditLog>>('/api/audit', {
        query: { action: action.trim(), targetType, result, pageSize: 200 },
      });
      setLogs(page.items);
      setError(null);
    } catch (fetchError) {
      setError(errorMessage(fetchError));
    } finally {
      setLoading(false);
    }
  }, [action, targetType, result]);

  useEffect(() => {
    void load();
  }, [load]);

  const prune = async (trigger: HTMLElement) => {
    const confirmed = await overlay.confirm({
      placementKey: 'audit.prune',
      trigger,
      title: '清理过期审计日志？',
      description: '默认保留 180 天。清理不可撤销，但只会删除早于保留期的记录。',
      confirmText: '清理',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      const result2 = await api.post<{ removed: number; retentionDays: number }>('/api/audit/prune', { retentionDays: 180 });
      toast.ok('清理完成', `已删除 ${result2.removed} 条早于 ${result2.retentionDays} 天的记录。`);
      await load();
    } catch (pruneError) {
      toast.critical('清理失败', errorMessage(pruneError));
    }
  };

  const columns: Column<AuditLog>[] = [
    {
      key: 'at',
      header: '时间',
      width: '11rem',
      render: (log) => (
        <span className="text-[11px] text-fg-muted" title={formatDateTime(log.at)}>
          {relativeTime(log.at)}
        </span>
      ),
    },
    {
      key: 'actor',
      header: '操作者',
      width: '14rem',
      render: (log) => <span className="text-[11px] text-fg">{log.actorEmail || '系统'}</span>,
    },
    {
      key: 'action',
      header: '动作',
      render: (log) => <code className="font-mono text-[11px] text-fg-muted">{log.action}</code>,
    },
    {
      key: 'target',
      header: '对象',
      width: '12rem',
      hideOnMobile: true,
      render: (log) => (
        <span className="text-[11px] text-fg-muted">
          {log.targetType}
          {log.targetId ? ` · ${log.targetId.slice(0, 12)}…` : ''}
        </span>
      ),
    },
    {
      key: 'result',
      header: '结果',
      width: '6rem',
      render: (log) => (
        <Badge tone={log.result === 'success' ? 'ok' : log.result === 'denied' ? 'caution' : 'critical'}>
          {log.result === 'success' ? '成功' : log.result === 'denied' ? '被拒绝' : '失败'}
        </Badge>
      ),
    },
    {
      key: 'ip',
      header: '来源 IP',
      width: '10rem',
      hideOnMobile: true,
      render: (log) => <span className="font-mono text-[11px] text-fg-subtle">{log.ip ?? '—'}</span>,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="审计日志"
        description="登录记录与管理员动作。创意的流转历史在各自的详情页里，不在这里重复记录。"
        actions={
          canPrune ? (
            <Button variant="default" onClick={(event) => void prune(event.currentTarget)}>
              清理过期记录
            </Button>
          ) : null
        }
      />

      <Card>
        <CardBody className="flex flex-wrap items-end gap-2">
          <div className="w-44">
            <TextInput
              label="动作前缀"
              placeholder="例如 auth.login"
              value={action}
              onChange={(event) => setAction(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void load();
              }}
            />
          </div>
          <div className="w-40">
            <Select
              label="对象类型"
              value={targetType}
              placeholder="全部"
              onChange={(event) => setTargetType(event.target.value)}
              options={(['auth', 'user', 'idea', 'settings', 'system'] as AuditTargetType[]).map((value) => ({ value, label: value }))}
            />
          </div>
          <div className="w-40">
            <Select
              label="结果"
              value={result}
              placeholder="全部"
              onChange={(event) => setResult(event.target.value)}
              options={(['success', 'denied', 'failure'] as AuditResult[]).map((value) => ({ value, label: value }))}
            />
          </div>
          <Button variant="default" loading={loading} onClick={() => void load()}>
            查询
          </Button>
        </CardBody>
      </Card>

      {error ? <p className="text-xs text-critical">{error}</p> : null}

      <Card>
        <CardHeader>
          <CardTitle subtitle={`最近 ${logs.length} 条`}>记录</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {loading && logs.length === 0 ? (
            <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
              <Spinner /> 正在加载…
            </div>
          ) : logs.length === 0 ? (
            <EmptyState title="没有匹配的记录" description="调整筛选条件或等待新的动作产生。" />
          ) : (
            <DataTable rows={logs} columns={columns} getRowId={(log) => log.id} dense />
          )}
        </CardBody>
      </Card>
    </PageContainer>
  );
}
